import { useState, useRef, useEffect, useCallback, useMemo, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Trash2, Plus, Edit2, Save, X, Camera, Loader2, CheckCircle2, Star, Copy, ImageIcon, ImagePlus, Eye, Languages, MoveRight, LayoutGrid, ChevronUp, ChevronDown, GripVertical, MoreVertical, ChevronsUp, ChevronsDown, Wrench, Sparkles, Zap, HelpCircle } from "lucide-react";
import { useChecklists, useChecklistById, useCreateChecklist, useUpdateChecklist, useDeleteChecklist, useSetDefaultChecklist, Checklist } from "@/hooks/useChecklists";
import { usePropertyPreventativeMaintenance } from "@/hooks/usePreventativeMaintenance";
import { useTaskReferencePhotos, useUploadTaskReferencePhoto, useDeleteTaskReferencePhoto, getPhotosForTask, TaskReferencePhoto } from "@/hooks/useTaskReferencePhotos";
import TaskReferencePhotoGallery from "./TaskReferencePhotoGallery";
import { useChecklistTranslation } from "@/hooks/useChecklistTranslation";
import { toast } from "@/hooks/use-toast";
import ReferencePhotosSection from "./ReferencePhotosSection";
import ChecklistPreviewModal from "./ChecklistPreviewModal";
import MoveTaskModal from "./MoveTaskModal";
import ApplyToRoomsModal from "./ApplyToRoomsModal";
import { RoomPickerModal } from "./RoomPickerModal";
import { formatDistanceToNow } from "date-fns";
import ChecklistLocalControls from "./ChecklistLocalControls";
import RoomManagementMenu from "./RoomManagementMenu";
import DeleteRoomDialog from "./DeleteRoomDialog";
import RenameRoomDialog from "./RenameRoomDialog";
import EditingGuardDialog from "./EditingGuardDialog";
import StandardsAndProofSection from "./StandardsAndProofSection";
import GuestAmenityStagingEditor from "./GuestAmenityStagingEditor";
import { useMoveTaskPhotos } from "@/hooks/useMoveTask";
import { Room, Task, TaskObject, CleaningCategory, CLEANING_CATEGORY_LABELS, ChecklistType, CHECKLIST_TYPE_LABELS, type GuestAmenityStagingLine, getTaskText, normalizeTask, getTaskType, getCleaningSettings, getMaintenanceSettings, DEFAULT_CLEANING_SETTINGS, DEFAULT_MAINTENANCE_SETTINGS } from "@/types/checklist";
import {
  parseAccessVerificationSettings,
  type AccessVerificationSettings,
  DEFAULT_ACCESS_VERIFICATION_SETTINGS,
  type ChecklistEntryType,
} from "@/types/lifecycle";
import { Json } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import { usePropertyRooms, useSetChecklistRoomLinks } from "@/hooks/usePropertyRooms";
import { parseGuestAmenityStagingFromChecklist } from "@/lib/guestAmenityStaging";
import { resolveRoomsToPropertyRoomsForProperty } from "@/lib/propertyChecklistRooms";
import { queryKeys } from "@/lib/queryKeys";
import { useProofStandards, useCreateProofStandard } from "@/hooks/useProofStandards";
import { useReferencePhotos } from "@/hooks/useReferencePhotos";
import { AiGenerateChecklistBanner } from "./AiGenerateChecklistBanner";
import { GenerateChecklistFromPhotosModal } from "./GenerateChecklistFromPhotosModal";
import { collectListingScanPhotos, countListingScanRooms } from "@/lib/propertyListingPhotos";
import {
  buildCleaningTaskRoomsFromScan,
  cleaningItemsFromScan,
  insertTaskReferencePhotoFromUrl,
  mergeChecklistRooms,
  unitCheckItemsFromScan,
  type GenerateChecklistItem,
} from "@/lib/applyListingScanToChecklist";
import { ensurePropertyRooms } from "@/lib/listingPhotoScan";

interface ChecklistTabProps {
  propertyId: string;
  /** When set (e.g. from Maintenance tab "Edit"), open this checklist in edit mode. Excluded from main list; only turnover checklists show in the tab bar by default. */
  editChecklistId?: string | null;
  onClearEditChecklistId?: () => void;
}

/** Tab values for nested checklist edit builder (owner Checklist tab). */
const CHECKLIST_BUILDER_TAB = {
  basics: "basics",
  arrival: "arrival",
  guest: "guest",
  proof: "proof",
  rooms: "rooms",
  reference: "reference",
} as const;

const ChecklistTab = ({ propertyId, editChecklistId, onClearEditChecklistId }: ChecklistTabProps) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: checklists, isLoading } = useChecklists(propertyId);
  const { data: propertyPpm = [] } = usePropertyPreventativeMaintenance(propertyId);
  const pmChecklistIds = new Set((propertyPpm ?? []).map((p) => p.checklist_id).filter(Boolean) as string[]);
  const { data: editChecklist } = useChecklistById(editChecklistId ?? undefined);

  const turnoverChecklists = (checklists && Array.isArray(checklists)) ? checklists.filter((c) => !pmChecklistIds.has(c.id)) : [];
  const displayChecklists: Checklist[] =
    editChecklistId && editChecklist && !turnoverChecklists.some((c) => c.id === editChecklist.id)
      ? [...turnoverChecklists, editChecklist]
      : turnoverChecklists;
  const [selectedChecklistId, setSelectedChecklistId] = useState<string | null>(null);
  const { data: taskPhotosData } = useTaskReferencePhotos(propertyId, selectedChecklistId);
  const taskPhotos = taskPhotosData?.list;
  const { data: proofStandards = [] } = useProofStandards(selectedChecklistId ?? undefined);
  const createProofStandard = useCreateProofStandard();
  const { data: referencePhotosResult } = useReferencePhotos(propertyId, selectedChecklistId);
  const createChecklist = useCreateChecklist();
  const updateChecklist = useUpdateChecklist();
  const deleteChecklist = useDeleteChecklist();
  const setDefaultChecklist = useSetDefaultChecklist();
  const uploadTaskPhoto = useUploadTaskReferencePhoto();
  const deleteTaskPhoto = useDeleteTaskReferencePhoto();
  const { translateRooms, isTranslating } = useChecklistTranslation();
  const { data: inventoryRoomsList = [] } = useInventoryRooms(propertyId);
  const { data: propertyRoomsList = [] } = usePropertyRooms(propertyId);
  const setChecklistRoomLinks = useSetChecklistRoomLinks();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Create modal state
  const [newChecklistName, setNewChecklistName] = useState("");
  const [newChecklistType, setNewChecklistType] = useState<ChecklistType>("cleaning");
  const [newChecklistDescription, setNewChecklistDescription] = useState("");
  const [cloneFromId, setCloneFromId] = useState<string>("");
  /** When starting fresh, selected property room ids for the new checklist. */
  const [createModalSelectedRoomIds, setCreateModalSelectedRoomIds] = useState<string[]>([]);
  const [createModalRoomPickerOpen, setCreateModalRoomPickerOpen] = useState(false);
  
  // Edit state
  const [editingName, setEditingName] = useState("");
  const [editingType, setEditingType] = useState<ChecklistType>("cleaning");
  const [editingDescription, setEditingDescription] = useState("");
  const [editingGuestAmenityStaging, setEditingGuestAmenityStaging] = useState<GuestAmenityStagingLine[]>([]);
  const [editingAccess, setEditingAccess] = useState<AccessVerificationSettings>({
    ...DEFAULT_ACCESS_VERIFICATION_SETTINGS,
  });
  const [rooms, setRooms] = useState<Room[]>([]);
  
  const [uploadingTask, setUploadingTask] = useState<string | null>(null);
  const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [deleteChecklistId, setDeleteChecklistId] = useState<string | null>(null);
  const [moveTaskData, setMoveTaskData] = useState<{ task: TaskObject; roomName: string } | null>(null);
  const [applyToRoomsData, setApplyToRoomsData] = useState<{ task: TaskObject; roomName: string } | null>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const moveTaskPhotos = useMoveTaskPhotos();
  const keyLocationRefInputRef = useRef<HTMLInputElement>(null);
  const [uploadingKeyLocationRefs, setUploadingKeyLocationRefs] = useState(false);

  // New room UX state
  const [newlyAddedRoomIndex, setNewlyAddedRoomIndex] = useState<number | null>(null);
  const [activeRoomTab, setActiveRoomTab] = useState<number | null>(null);
  const [checklistBuilderTab, setChecklistBuilderTab] = useState<string>(CHECKLIST_BUILDER_TAB.basics);
  const [collapsedRooms, setCollapsedRooms] = useState<Set<number>>(new Set());
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);
  const [aiGenerateOpen, setAiGenerateOpen] = useState(false);
  const [aiGenerateApplying, setAiGenerateApplying] = useState(false);
  const [deleteRoomIndex, setDeleteRoomIndex] = useState<number | null>(null);
  const [renameRoomIndex, setRenameRoomIndex] = useState<number | null>(null);
  const [pendingChecklistSwitch, setPendingChecklistSwitch] = useState<string | null>(null);
  const [pendingCreateChecklist, setPendingCreateChecklist] = useState(false);
  const [pendingCancelExit, setPendingCancelExit] = useState(false);
  const initialEditSnapshotRef = useRef<{
    name: string;
    description: string;
    guestStagingJson: string;
    roomsJson: string;
    accessJson: string;
  } | null>(null);
  const lastLoadedEditChecklistIdRef = useRef<string | null>(null);
  const roomRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const isManualScrollRef = useRef(false);

  // Auto-select first checklist or default
  useEffect(() => {
    if (displayChecklists.length > 0 && !selectedChecklistId) {
      const defaultChecklist = displayChecklists.find(c => c.is_default);
      setSelectedChecklistId(defaultChecklist?.id || displayChecklists[0].id);
    }
  }, [displayChecklists, selectedChecklistId]);

  // When opened with editChecklistId (e.g. from Maintenance "Edit" or after Create), select that checklist and enter edit mode.
  // Only load form data when editChecklistId *changes* (user opened a different checklist), not when editChecklist refetches,
  // so local edits (e.g. task type) are not overwritten by stale server data.
  useEffect(() => {
    if (!editChecklistId || !editChecklist) return;
    const alreadyLoadedThisChecklist = lastLoadedEditChecklistIdRef.current === editChecklistId;
    lastLoadedEditChecklistIdRef.current = editChecklistId;
    setSelectedChecklistId(editChecklistId);
    if (!alreadyLoadedThisChecklist) {
      setEditingName(editChecklist.name);
      setEditingType((editChecklist as { type?: string }).type === "maintenance" ? "maintenance" : "cleaning");
      setEditingDescription(editChecklist.description || "");
      const guestStaging = parseGuestAmenityStagingFromChecklist(editChecklist.guest_amenity_staging);
      setEditingGuestAmenityStaging(guestStaging);
      const accessParsed = parseAccessVerificationSettings(editChecklist.access_verification_settings);
      setEditingAccess(accessParsed);
      try {
        const roomsData = (editChecklist.rooms as Room[]) || [];
        const normalizedRooms = roomsData.map((room) => ({
          ...room,
          tasks: (room.tasks || []).map((task) => normalizeTask(task)),
        }));
        setRooms(normalizedRooms);
        initialEditSnapshotRef.current = {
          name: editChecklist.name,
          description: editChecklist.description || "",
          guestStagingJson: JSON.stringify(guestStaging),
          roomsJson: JSON.stringify(normalizedRooms),
          accessJson: JSON.stringify(accessParsed),
        };
      } catch {
        setRooms([]);
        initialEditSnapshotRef.current = {
          name: editChecklist.name,
          description: editChecklist.description || "",
          guestStagingJson: JSON.stringify(guestStaging),
          roomsJson: "[]",
          accessJson: JSON.stringify(accessParsed),
        };
      }
      setNewlyAddedRoomIndex(null);
      setActiveRoomTab(null);
      setCollapsedRooms(new Set());
      const builderFromUrl = searchParams.get("checklistBuilderTab");
      setChecklistBuilderTab(
        builderFromUrl === "guest" ? CHECKLIST_BUILDER_TAB.guest : CHECKLIST_BUILDER_TAB.basics,
      );
      setLastSavedAt(null);
    }
    setIsEditing(true);
  }, [editChecklistId, editChecklist, searchParams]);

  // Slice 45 — jump to Guest Amenities when Inventory CTA sets checklistBuilderTab=guest
  useEffect(() => {
    if (!isEditing) return;
    if (searchParams.get("checklistBuilderTab") === "guest") {
      setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.guest);
      const el = document.getElementById(
        selectedChecklistId ? `checklist-edit-guest-amenities-${selectedChecklistId}` : "",
      );
      if (el) {
        requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
      }
    }
  }, [isEditing, searchParams, selectedChecklistId]);

  // Maintenance checklists have no arrival tab; reference tab requires rooms.
  useEffect(() => {
    if (editingType !== "cleaning" && checklistBuilderTab === CHECKLIST_BUILDER_TAB.arrival) {
      setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.basics);
    }
  }, [editingType, checklistBuilderTab]);

  useEffect(() => {
    if (rooms.length === 0 && checklistBuilderTab === CHECKLIST_BUILDER_TAB.reference) {
      setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.rooms);
    }
  }, [rooms.length, checklistBuilderTab]);

  useEffect(() => {
    if (checklistBuilderTab === "final") {
      setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.basics);
    }
  }, [checklistBuilderTab]);

  // IntersectionObserver for auto-highlighting active room (with debounce)
  useEffect(() => {
    if (!isEditing || rooms.length === 0 || checklistBuilderTab !== CHECKLIST_BUILDER_TAB.rooms) return;

    const observers: IntersectionObserver[] = [];
    const visibleRooms = new Map<number, number>();
    let debounceTimer: ReturnType<typeof setTimeout>;

    Object.entries(roomRefs.current).forEach(([indexStr, element]) => {
      if (!element) return;
      const index = parseInt(indexStr);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (isManualScrollRef.current) return; // Skip during manual scroll
            
            if (entry.isIntersecting) {
              visibleRooms.set(index, entry.intersectionRatio);
            } else {
              visibleRooms.delete(index);
            }

            // Debounced update to prevent jitter
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
              // Find room with highest intersection ratio
              let maxRatio = 0;
              let activeIndex: number | null = null;
              visibleRooms.forEach((ratio, idx) => {
                if (ratio > maxRatio) {
                  maxRatio = ratio;
                  activeIndex = idx;
                }
              });

              if (activeIndex !== null) {
                setActiveRoomTab(activeIndex);
              }
            }, 50);
          });
        },
        {
          root: null,
          rootMargin: '-80px 0px -40% 0px',
          threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
        }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      clearTimeout(debounceTimer);
      observers.forEach((obs) => obs.disconnect());
    };
  }, [isEditing, rooms.length, checklistBuilderTab]);

  // Auto-scroll to newly added room and clear highlight after animation
  useEffect(() => {
    if (newlyAddedRoomIndex !== null && roomRefs.current[newlyAddedRoomIndex]) {
      setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.rooms);
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        roomRefs.current[newlyAddedRoomIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        setActiveRoomTab(newlyAddedRoomIndex);
      }, 50);

      // Clear highlight after animation completes
      const timer = setTimeout(() => {
        setNewlyAddedRoomIndex(null);
      }, 2100);

      return () => clearTimeout(timer);
    }
  }, [newlyAddedRoomIndex]);

  const selectedChecklist = displayChecklists.find(c => c.id === selectedChecklistId);

  const listingScanPhotos = useMemo(() => {
    const propertyRoomNames = propertyRoomsList.map((r) => r.name);
    const fallbackNames = rooms.map((r) => r.name.trim()).filter(Boolean);
    const refs = referencePhotosResult?.list ?? [];
    return collectListingScanPhotos(refs, propertyRoomNames.length > 0 ? propertyRoomNames : fallbackNames);
  }, [referencePhotosResult?.list, propertyRoomsList, rooms]);

  const listingPhotoRoomCount = useMemo(
    () => countListingScanRooms(listingScanPhotos),
    [listingScanPhotos]
  );

  const getTaskPhotoKey = (roomName: string, taskIndex: number) => `${roomName}-${taskIndex}`;

  const getTaskPhotos = (roomName: string, taskText: string): TaskReferencePhoto[] => {
    return getPhotosForTask(taskPhotos ?? undefined, roomName.trim(), taskText.trim());
  };

  const resetEditForm = () => {
    setEditingName("");
    setEditingType("cleaning");
    setEditingDescription("");
    setEditingGuestAmenityStaging([]);
    setEditingAccess({ ...DEFAULT_ACCESS_VERIFICATION_SETTINGS });
    setRooms([]);
    setIsEditing(false);
    setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.basics);
    setNewlyAddedRoomIndex(null);
    setActiveRoomTab(null);
    setCollapsedRooms(new Set());
    initialEditSnapshotRef.current = null;
    lastLoadedEditChecklistIdRef.current = null;
    setLastSavedAt(null);
    onClearEditChecklistId?.();
  };

  const hasUnsavedChanges = useCallback(() => {
    const snap = initialEditSnapshotRef.current;
    if (!snap) return false;
    const currentRoomsJson = JSON.stringify(rooms);
    const accessJson = JSON.stringify(editingAccess);
    const currentGuestStagingJson = JSON.stringify(editingGuestAmenityStaging);
    return (
      editingName !== snap.name ||
      editingDescription !== snap.description ||
      currentGuestStagingJson !== snap.guestStagingJson ||
      currentRoomsJson !== snap.roomsJson ||
      accessJson !== snap.accessJson
    );
  }, [editingName, editingDescription, editingGuestAmenityStaging, rooms, editingAccess]);

  const handleCancelEdit = () => {
    if (hasUnsavedChanges()) {
      setPendingCancelExit(true);
    } else {
      resetEditForm();
    }
  };

  const resetCreateModal = () => {
    setNewChecklistName("");
    setNewChecklistType("cleaning");
    setNewChecklistDescription("");
    setCloneFromId("");
    setCreateModalSelectedRoomIds([]);
    setIsCreateModalOpen(false);
  };

  // Guarded checklist switching
  const handleChecklistSwitch = (newChecklistId: string) => {
    if (isEditing && newChecklistId !== selectedChecklistId) {
      setPendingChecklistSwitch(newChecklistId);
    } else {
      setSelectedChecklistId(newChecklistId);
    }
  };

  const handleCreateChecklistClick = () => {
    if (isEditing) {
      setPendingCreateChecklist(true);
    } else {
      setIsCreateModalOpen(true);
    }
  };

  const handleDiscardAndSwitch = () => {
    resetEditForm();
    if (pendingChecklistSwitch) {
      setSelectedChecklistId(pendingChecklistSwitch);
      setPendingChecklistSwitch(null);
    } else if (pendingCreateChecklist) {
      setPendingCreateChecklist(false);
      setIsCreateModalOpen(true);
    }
    setPendingCancelExit(false);
  };

  const handleStayEditing = () => {
    setPendingChecklistSwitch(null);
    setPendingCreateChecklist(false);
    setPendingCancelExit(false);
  };

  const startEditing = (checklist: Checklist) => {
    setEditingName(checklist.name);
    setEditingType((checklist as { type?: string }).type === "maintenance" ? "maintenance" : "cleaning");
    setEditingDescription(checklist.description || "");
    const guestStaging = parseGuestAmenityStagingFromChecklist(checklist.guest_amenity_staging);
    setEditingGuestAmenityStaging(guestStaging);
    const accessParsed = parseAccessVerificationSettings(checklist.access_verification_settings);
    setEditingAccess(accessParsed);
    // Normalize tasks to always be objects
    try {
      const roomsData = (checklist.rooms as Room[]) || [];
      const normalizedRooms = roomsData.map(room => ({
        ...room,
        tasks: (room.tasks || []).map(task => normalizeTask(task))
      }));
      setRooms(normalizedRooms);
      initialEditSnapshotRef.current = {
        name: checklist.name,
        description: checklist.description || "",
        guestStagingJson: JSON.stringify(guestStaging),
        roomsJson: JSON.stringify(normalizedRooms),
        accessJson: JSON.stringify(accessParsed),
      };
      setIsEditing(true);
    } catch (error) {
      console.error("Error starting edit:", error);
      setRooms([]);
      initialEditSnapshotRef.current = {
        name: checklist.name,
        description: checklist.description || "",
        guestStagingJson: JSON.stringify(guestStaging),
        roomsJson: "[]",
        accessJson: JSON.stringify(accessParsed),
      };
      setIsEditing(true);
    }
    setNewlyAddedRoomIndex(null);
    setActiveRoomTab(null);
    setCollapsedRooms(new Set());
    setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.basics);
    setLastSavedAt(null);
  };

  const scrollToRoom = useCallback((index: number) => {
    setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.rooms);
    isManualScrollRef.current = true;
    setActiveRoomTab(index);

    setCollapsedRooms((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });

    const runScroll = () => {
      roomRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };
    requestAnimationFrame(() => {
      setTimeout(() => {
        runScroll();
        setTimeout(() => {
          isManualScrollRef.current = false;
        }, 500);
      }, 100);
    });
  }, []);

  const toggleRoomCollapsed = (index: number) => {
    setCollapsedRooms(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const expandAllRooms = () => {
    setCollapsedRooms(new Set());
  };

  const collapseAllRooms = () => {
    setCollapsedRooms(new Set(rooms.map((_, i) => i)));
  };

  const addRoom = () => {
    setIsAddRoomModalOpen(true);
  };

  /** Add a room from the canonical picker (Rooms tab source of truth). */
  const addRoomFromPicker = (room: { id: string; name: string }) => {
    const newIndex = rooms.length;
    setRooms([
      ...rooms,
      {
        name: room.name,
        tasks: [],
        property_room_id: room.id,
      },
    ]);
    setNewlyAddedRoomIndex(newIndex);
    setIsAddRoomModalOpen(false);
  };

  const updateRoomName = (index: number, name: string) => {
    const updated = [...rooms];
    updated[index].name = name;
    setRooms(updated);
  };

  const updateRoomDetails = (
    index: number,
    details: { name?: string; entry_code?: string | null; entry_notes?: string | null }
  ) => {
    const updated = [...rooms];
    if (details.name !== undefined) updated[index].name = details.name;
    if (details.entry_code !== undefined) updated[index].entry_code = details.entry_code;
    if (details.entry_notes !== undefined) updated[index].entry_notes = details.entry_notes;
    setRooms(updated);
  };

  const removeRoom = (index: number) => {
    setRooms(rooms.filter((_, i) => i !== index));
    setDeleteRoomIndex(null);
  };

  const moveRoomUp = (index: number) => {
    if (index === 0) return;
    const updated = [...rooms];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setRooms(updated);
    // Update active tab to follow the moved room
    if (activeRoomTab === index) setActiveRoomTab(index - 1);
    else if (activeRoomTab === index - 1) setActiveRoomTab(index);
  };

  const moveRoomDown = (index: number) => {
    if (index >= rooms.length - 1) return;
    const updated = [...rooms];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setRooms(updated);
    // Update active tab to follow the moved room
    if (activeRoomTab === index) setActiveRoomTab(index + 1);
    else if (activeRoomTab === index + 1) setActiveRoomTab(index);
  };

  const duplicateRoom = (index: number) => {
    const roomToDuplicate = rooms[index];
    const newRoom: Room = {
      name: `${roomToDuplicate.name} (Copy)`,
      name_en: roomToDuplicate.name_en,
      name_es: roomToDuplicate.name_es,
      tasks: roomToDuplicate.tasks.map(task => {
        const normalized = normalizeTask(task);
        return {
          text: normalized.text,
          text_en: normalized.text_en,
          text_es: normalized.text_es,
          task_type: normalized.task_type,
          subcategory: normalized.subcategory,
          guest_restock_quantity: normalized.guest_restock_quantity ?? null,
          require_reference_photo: normalized.require_reference_photo,
          require_before_photo: normalized.require_before_photo,
          require_after_photo: normalized.require_after_photo,
          cleaning_settings: normalized.cleaning_settings,
          maintenance_settings: normalized.maintenance_settings,
          quick_check_settings: normalized.quick_check_settings,
        };
      }),
      entry_code: roomToDuplicate.entry_code ?? undefined,
      entry_notes: roomToDuplicate.entry_notes ?? undefined,
    };
    const updated = [...rooms];
    updated.splice(index + 1, 0, newRoom);
    setRooms(updated);
    setNewlyAddedRoomIndex(index + 1);
  };

  const handleAiGenerateApply = async (items: GenerateChecklistItem[]) => {
    if (!selectedChecklistId) return;
    setAiGenerateApplying(true);
    try {
      const propertyRoomNames = propertyRoomsList.map((r) => r.name);
      const roomNamesFromItems = [...new Set(items.map((i) => i.roomName.trim()).filter(Boolean))];
      await ensurePropertyRooms(
        propertyId,
        roomNamesFromItems.length > 0 ? roomNamesFromItems : propertyRoomNames
      );

      const unitItems = unitCheckItemsFromScan(items);
      let order = proofStandards.length;
      for (const item of unitItems) {
        await createProofStandard.mutateAsync({
          property_id: propertyId,
          checklist_id: selectedChecklistId,
          title: item.name.trim(),
          type: "proof_photo",
          room: item.roomName.trim() || null,
          reference_media_urls: item.sourcePhotoUrl ? [item.sourcePhotoUrl] : [],
          priority: "high_value",
          display_order: order++,
        });
      }

      const cleaning = cleaningItemsFromScan(items);
      const { rooms: taskRooms, refLinks } = buildCleaningTaskRoomsFromScan(cleaning, propertyRoomNames);
      setRooms((prev) => mergeChecklistRooms(prev, taskRooms));

      for (const link of refLinks) {
        await insertTaskReferencePhotoFromUrl({
          propertyId,
          checklistId: selectedChecklistId,
          roomName: link.roomName,
          taskText: link.taskText,
          photoUrl: link.photoUrl,
        });
      }

      const parts: string[] = [];
      if (unitItems.length > 0) {
        parts.push(`${unitItems.length} Unit Check item${unitItems.length !== 1 ? "s" : ""}`);
      }
      if (cleaning.length > 0) {
        parts.push(`${cleaning.length} cleaning task${cleaning.length !== 1 ? "s" : ""}`);
      }
      toast({
        title: "Added to checklist",
        description:
          (parts.length > 0 ? parts.join(" · ") : "Items added") +
          ". Tap Save to sync everything.",
      });
      setAiGenerateOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add generated items",
        variant: "destructive",
      });
    } finally {
      setAiGenerateApplying(false);
    }
  };

  const addTask = (roomIndex: number) => {
    const updated = [...rooms];
    updated[roomIndex].tasks.push({
      text: "",
      require_reference_photo: false,
      require_before_photo: false,
      require_after_photo: false,
      cleaning_settings: { ...DEFAULT_CLEANING_SETTINGS },
      maintenance_settings: { ...DEFAULT_MAINTENANCE_SETTINGS },
    });
    setRooms(updated);
  };

  const updateTaskCleaningCategory = (roomIndex: number, taskIndex: number, category: CleaningCategory) => {
    setRooms((prev) =>
      prev.map((room, ri) =>
        ri !== roomIndex
          ? room
          : {
              ...room,
              tasks: room.tasks.map((t, ti) => {
                if (ti !== taskIndex) return t;
                const norm = normalizeTask(t);
                const guestQty = category === "guest_amenities" ? norm.guest_restock_quantity ?? null : null;
                return typeof t === "string"
                  ? { text: t as string, subcategory: category, guest_restock_quantity: guestQty }
                  : { ...norm, subcategory: category, guest_restock_quantity: guestQty };
              }),
            }
      )
    );
  };

  const updateTaskGuestRestockQuantity = (roomIndex: number, taskIndex: number, raw: string) => {
    const updated = [...rooms];
    const task = normalizeTask(updated[roomIndex].tasks[taskIndex]);
    const trimmed = raw.trim();
    let qty: number | null = null;
    if (trimmed !== "") {
      const n = parseInt(trimmed, 10);
      if (!Number.isNaN(n)) qty = Math.min(999, Math.max(1, n));
    }
    updated[roomIndex].tasks[taskIndex] = { ...task, guest_restock_quantity: qty };
    setRooms(updated);
  };

  const updateMaintenanceSetting = (
    roomIndex: number,
    taskIndex: number,
    field: keyof MaintenanceSettings,
    value: boolean | string
  ) => {
    const updated = [...rooms];
    const task = normalizeTask(updated[roomIndex].tasks[taskIndex]);
    const currentSettings = task.maintenance_settings || { ...DEFAULT_MAINTENANCE_SETTINGS };
    updated[roomIndex].tasks[taskIndex] = { 
      ...task, 
      maintenance_settings: { ...currentSettings, [field]: value } 
    };
    setRooms(updated);
  };

  const updateTaskText = (roomIndex: number, taskIndex: number, value: string) => {
    const updated = [...rooms];
    const task = normalizeTask(updated[roomIndex].tasks[taskIndex]);
    updated[roomIndex].tasks[taskIndex] = { ...task, text: value };
    setRooms(updated);
  };

  const updateTaskPhotoRequirement = (
    roomIndex: number,
    taskIndex: number,
    field: 'require_reference_photo' | 'require_before_photo' | 'require_after_photo',
    value: boolean
  ) => {
    const updated = [...rooms];
    const task = normalizeTask(updated[roomIndex].tasks[taskIndex]);
    // Update both legacy field and cleaning_settings to keep them in sync
    updated[roomIndex].tasks[taskIndex] = { 
      ...task, 
      [field]: value,
      cleaning_settings: {
        ...task.cleaning_settings,
        [field]: value
      }
    };
    setRooms(updated);
  };

  const removeTask = (roomIndex: number, taskIndex: number) => {
    const updated = [...rooms];
    updated[roomIndex].tasks = updated[roomIndex].tasks.filter((_, i) => i !== taskIndex);
    setRooms(updated);
  };

  // Reorder task within a room
  const moveTaskUp = (roomIndex: number, taskIndex: number) => {
    if (taskIndex === 0) return; // Already at top
    const updated = [...rooms];
    const tasks = [...updated[roomIndex].tasks];
    [tasks[taskIndex - 1], tasks[taskIndex]] = [tasks[taskIndex], tasks[taskIndex - 1]];
    updated[roomIndex].tasks = tasks;
    setRooms(updated);
  };

  const moveTaskDown = (roomIndex: number, taskIndex: number) => {
    const updated = [...rooms];
    if (taskIndex >= updated[roomIndex].tasks.length - 1) return; // Already at bottom
    const tasks = [...updated[roomIndex].tasks];
    [tasks[taskIndex], tasks[taskIndex + 1]] = [tasks[taskIndex + 1], tasks[taskIndex]];
    updated[roomIndex].tasks = tasks;
    setRooms(updated);
  };

  const moveTaskToTop = (roomIndex: number, taskIndex: number) => {
    if (taskIndex === 0) return; // Already at top
    const updated = [...rooms];
    const tasks = [...updated[roomIndex].tasks];
    const [task] = tasks.splice(taskIndex, 1);
    tasks.unshift(task);
    updated[roomIndex].tasks = tasks;
    setRooms(updated);
  };

  const moveTaskToBottom = (roomIndex: number, taskIndex: number) => {
    const updated = [...rooms];
    if (taskIndex >= updated[roomIndex].tasks.length - 1) return; // Already at bottom
    const tasks = [...updated[roomIndex].tasks];
    const [task] = tasks.splice(taskIndex, 1);
    tasks.push(task);
    updated[roomIndex].tasks = tasks;
    setRooms(updated);
  };

  // Move task from one room to another (within current editing state)
  const handleMoveTask = async (taskText: string, fromRoomName: string, toRoomName: string) => {
    // Find source and target room indices
    const fromRoomIndex = rooms.findIndex(r => r.name === fromRoomName);
    const toRoomIndex = rooms.findIndex(r => r.name === toRoomName);
    
    if (fromRoomIndex === -1 || toRoomIndex === -1) {
      throw new Error("Room not found");
    }

    // Find the task in source room
    const taskIndex = rooms[fromRoomIndex].tasks.findIndex(t => {
      const normalized = normalizeTask(t);
      return normalized.text === taskText;
    });

    if (taskIndex === -1) {
      throw new Error("Task not found");
    }

    const taskToMove = rooms[fromRoomIndex].tasks[taskIndex];

    // Update rooms state (move task)
    const updatedRooms = [...rooms];
    // Remove from source room
    updatedRooms[fromRoomIndex].tasks = updatedRooms[fromRoomIndex].tasks.filter((_, i) => i !== taskIndex);
    // Add to target room (at end)
    updatedRooms[toRoomIndex].tasks = [...updatedRooms[toRoomIndex].tasks, taskToMove];
    setRooms(updatedRooms);

    // Update photos in database (room_name field)
    await moveTaskPhotos.mutateAsync({
      propertyId,
      taskText,
      fromRoomName,
      toRoomName,
    });
  };

  const handleTaskPhotoUpload = async (roomName: string, taskText: string, taskKey: string, files: File[]) => {
    if (!roomName.trim() || !taskText.trim()) {
      toast({ title: "Error", description: "Please enter room name and task text before uploading a photo", variant: "destructive" });
      return;
    }

    setUploadingTask(taskKey);
    try {
      // Get current count of photos for this task to determine starting display_order
      const existingPhotos = getTaskPhotos(roomName.trim(), taskText.trim());
      const startingOrder = existingPhotos.length;
      
      // Upload all files sequentially with incrementing display_order
      for (let i = 0; i < files.length; i++) {
        await uploadTaskPhoto.mutateAsync({
          propertyId,
          roomName: roomName.trim(),
          taskText: taskText.trim(),
          file: files[i],
          displayOrder: startingOrder + i,
          checklistId: selectedChecklistId ?? undefined,
        });
      }
      toast({ title: "Success", description: `${files.length} reference photo${files.length > 1 ? 's' : ''} uploaded` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload photo", variant: "destructive" });
    } finally {
      setUploadingTask(null);
    }
  };

  const handleDeleteTaskPhoto = async (photo: TaskReferencePhoto) => {
    try {
      await deleteTaskPhoto.mutateAsync({ id: photo.id, propertyId, photoUrl: photo.photo_url, checklistId: selectedChecklistId ?? undefined });
      toast({ title: "Success", description: "Reference photo deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete photo", variant: "destructive" });
    }
  };

  const handleCreateChecklist = async () => {
    if (!newChecklistName.trim()) {
      toast({ title: "Error", description: "Checklist name is required", variant: "destructive" });
      return;
    }

    let initialRooms: Room[] = [];
    if (cloneFromId && cloneFromId !== "none") {
      const sourceChecklist = displayChecklists.find(c => c.id === cloneFromId);
      if (sourceChecklist) {
        initialRooms = (sourceChecklist.rooms as Room[]) || [];
      }
    } else if (createModalSelectedRoomIds.length > 0 && propertyRoomsList.length > 0) {
      initialRooms = createModalSelectedRoomIds
        .map((id) => propertyRoomsList.find((pr) => pr.id === id))
        .filter(Boolean)
        .map((pr) => ({ name: pr!.name, tasks: [], property_room_id: pr!.id }));
    }

    try {
      const { rooms: roomsWithPropertyRooms, propertyRoomIds, roomNamesOriginal } = await resolveRoomsToPropertyRooms(initialRooms);
      const result = await createChecklist.mutateAsync({
        propertyId,
        name: newChecklistName.trim(),
        type: newChecklistType,
        description: newChecklistDescription.trim() || undefined,
        rooms: roomsWithPropertyRooms,
        isDefault: displayChecklists.length === 0,
      });
      if (propertyRoomIds.length > 0) {
        await setChecklistRoomLinks.mutateAsync({
          checklistId: result.id,
          propertyId,
          propertyRoomIds,
          roomNamesOriginal,
        });
      }
      toast({ title: "Success", description: "Checklist created" });
      resetCreateModal();
      setSelectedChecklistId(result.id);
      // Keep new checklist open in edit mode and prompt to add room/tasks
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "checklist");
        next.set("editChecklistId", result.id);
        return next;
      });
    } catch (error) {
      const description =
        error instanceof Error
          ? error.message
          : typeof error === "object" &&
              error !== null &&
              "message" in error &&
              typeof (error as { message: unknown }).message === "string"
            ? (error as { message: string }).message
            : "Failed to create checklist";
      toast({ title: "Error", description, variant: "destructive" });
    }
  };

  /** Resolve each room to canonical property_rooms (find by name or create). Does NOT create inventory_rooms. */
  const resolveRoomsToPropertyRooms = async (roomsToResolve: Room[]): Promise<{ rooms: Room[]; propertyRoomIds: string[]; roomNamesOriginal: (string | null)[] }> => {
    const result = await resolveRoomsToPropertyRoomsForProperty(propertyId, roomsToResolve, propertyRoomsList);
    await queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(propertyId) });
    await queryClient.invalidateQueries({ queryKey: ["inventory-rooms", propertyId] });
    await queryClient.invalidateQueries({ queryKey: queryKeys.checklists.byProperty(propertyId) });
    return result;
  };

  const handleKeyLocationRefFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    // Snapshot files before clearing the input — `FileList` is live and resets when value is cleared.
    const list = Array.from(input.files ?? []);
    input.value = "";
    if (!list.length) return;
    if (!selectedChecklistId) {
      toast({
        title: "Error",
        description: "Select a checklist before uploading.",
        variant: "destructive",
      });
      return;
    }
    setUploadingKeyLocationRefs(true);
    try {
      const newUrls: string[] = [];
      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 40);
        // Same bucket + path prefix as task reference photos so owner RLS applies (propertyId first segment).
        const path = `${propertyId}/key_location_refs/${selectedChecklistId}/${Date.now()}_${i}_${safeName}`;
        const { error } = await supabase.storage.from("reference-photos").upload(path, file, {
          contentType: file.type || `image/${ext === "jpg" ? "jpeg" : ext}`,
          upsert: true,
        });
        if (error) throw error;
        const { data } = supabase.storage.from("reference-photos").getPublicUrl(path);
        newUrls.push(data.publicUrl);
      }
      setEditingAccess((a) => ({
        ...a,
        key_location_reference_urls: [...(a.key_location_reference_urls ?? []), ...newUrls],
      }));
      toast({
        title: "Success",
        description: `${newUrls.length} reference photo${newUrls.length === 1 ? "" : "s"} added`,
      });
    } catch (err) {
      console.error(err);
      const msg =
        err && typeof err === "object" && "message" in err && typeof (err as { message: unknown }).message === "string"
          ? (err as { message: string }).message
          : (err as Error).message || "Failed to upload";
      toast({
        title: "Error",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setUploadingKeyLocationRefs(false);
    }
  };

  const handleRemoveKeyLocationRef = async (url: string) => {
    setEditingAccess((a) => ({
      ...a,
      key_location_reference_urls: (a.key_location_reference_urls ?? []).filter((u) => u !== url),
    }));
    try {
      const u = new URL(url);
      const refParts = u.pathname.split("/reference-photos/");
      if (refParts[1]) {
        await supabase.storage.from("reference-photos").remove([decodeURIComponent(refParts[1])]);
        return;
      }
      const legacy = u.pathname.split("/cleaning-photos/");
      if (legacy[1]) {
        await supabase.storage.from("cleaning-photos").remove([decodeURIComponent(legacy[1])]);
      }
    } catch {
      /* ignore storage cleanup failures */
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedChecklistId || !editingName.trim()) {
      toast({ title: "Error", description: "Checklist name is required", variant: "destructive" });
      return;
    }

    const cleanRooms = (rooms || [])
      .filter((r) => r && r.name && r.name.trim())
      .map((r) => ({ 
        ...r,
        name: r.name.trim(), 
        tasks: (r.tasks || [])
          .map(t => {
            try {
              return normalizeTask(t);
            } catch {
              return typeof t === 'string' ? { text: t } : t;
            }
          })
          .filter((t) => t && t.text && t.text.trim()) 
      }));

    try {
      const { rooms: roomsWithPropertyRooms, propertyRoomIds, roomNamesOriginal } = await resolveRoomsToPropertyRooms(cleanRooms);
      // Auto-translate to Spanish before saving
      const translatedRooms = await translateRooms(roomsWithPropertyRooms);

      const stagingToSave = editingGuestAmenityStaging
        .filter((l) => l.item_label.trim())
        .map((l) => ({
          ...l,
          subitems: (l.subitems ?? []).filter((s) => s.item_label.trim()),
        }))
        .map((l) => ({
          ...l,
          subitems: l.subitems && l.subitems.length > 0 ? l.subitems : undefined,
        }));

      await updateChecklist.mutateAsync({
        id: selectedChecklistId,
        propertyId,
        name: editingName.trim(),
        type: editingType,
        description: editingDescription.trim() || undefined,
        guest_amenity_staging: JSON.parse(JSON.stringify(stagingToSave)) as Json,
        rooms: translatedRooms,
        access_verification_settings: editingAccess as unknown as Json,
      });
      await setChecklistRoomLinks.mutateAsync({
        checklistId: selectedChecklistId,
        propertyId,
        propertyRoomIds,
        roomNamesOriginal,
      });
      setRooms(translatedRooms);
      setEditingGuestAmenityStaging(stagingToSave);
      const guestStagingJson = JSON.stringify(stagingToSave);
      initialEditSnapshotRef.current = {
        name: editingName.trim(),
        description: editingDescription.trim() || "",
        guestStagingJson,
        roomsJson: JSON.stringify(translatedRooms),
        accessJson: JSON.stringify(editingAccess),
      };
      setLastSavedAt(new Date());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["proof-standards", selectedChecklistId] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.photos.taskReference(propertyId, selectedChecklistId) }),
        queryClient.invalidateQueries({ queryKey: ["checklist-last-submission", propertyId, selectedChecklistId] }),
      ]);
      toast({ title: "Success", description: "Checklist updated and translated" });
      setIsEditing(false);
      setChecklistBuilderTab(CHECKLIST_BUILDER_TAB.basics);
      setActiveRoomTab(null);
      setNewlyAddedRoomIndex(null);
      setCollapsedRooms(new Set());
      lastLoadedEditChecklistIdRef.current = null;
      onClearEditChecklistId?.();
      setSearchParams((prev) => {
        if (!prev.get("editChecklistId")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("editChecklistId");
        return next;
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update checklist", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteChecklistId) return;
    
    if (displayChecklists.length === 1) {
      toast({ title: "Error", description: "Cannot delete the only checklist", variant: "destructive" });
      setDeleteChecklistId(null);
      return;
    }

    try {
      await deleteChecklist.mutateAsync({ id: deleteChecklistId, propertyId });
      toast({ title: "Success", description: "Checklist deleted" });
      setSelectedChecklistId(null);
      setDeleteChecklistId(null);
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete checklist", variant: "destructive" });
      setDeleteChecklistId(null);
    }
  };

  const handleSetDefault = async (checklistId: string) => {
    try {
      await setDefaultChecklist.mutateAsync({ checklistId, propertyId });
      toast({ title: "Success", description: "Default checklist updated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to set default checklist", variant: "destructive" });
    }
  };


  // Get all rooms from all checklists for reference photos
  const allRooms: Room[] = displayChecklists.flatMap((c) => {
    try {
      return (c.rooms as Room[]) || [];
    } catch {
      return [];
    }
  });
  const uniqueRooms = allRooms.filter(
    (room, index, self) => index === self.findIndex((r) => r.name === room.name)
  );

  const selectedRooms = (selectedChecklist?.rooms as Room[]) || [];

  /** Keeps Radix Tabs controlled on first paint (avoid value undefined then string). */
  const checklistTabsValue =
    displayChecklists.length > 0
      ? selectedChecklistId ??
        displayChecklists.find((c) => c.is_default)?.id ??
        displayChecklists[0]!.id
      : "";

  const savedDistanceHint =
    lastSavedAt && !hasUnsavedChanges() ? formatDistanceToNow(lastSavedAt, { addSuffix: true }) : null;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between" data-tour="tour-checklist-header">
        <h3 className="text-lg font-semibold">Checklists</h3>
        <Button onClick={handleCreateChecklistClick}>
          <Plus className="w-4 h-4 mr-2" /> Create Checklist
        </Button>
      </div>

      {displayChecklists.length === 0 ? (
        <Card data-tour="tour-checklist-empty">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground mb-4">No checklists yet. Create one to get started.</p>
            <Button onClick={handleCreateChecklistClick}>
              <Plus className="w-4 h-4 mr-2" /> Create Your First Checklist
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Checklist Tabs */}
          <Tabs value={checklistTabsValue} onValueChange={handleChecklistSwitch}>
            <TabsList className="w-full h-auto gap-1 p-1 flex flex-wrap justify-start">
              {displayChecklists.map((checklist) => (
                <TabsTrigger 
                  key={checklist.id} 
                  value={checklist.id}
                  className="flex items-center gap-1.5 text-xs sm:text-sm"
                >
                  {checklist.is_default && <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />}
                  <span className="truncate max-w-[120px] sm:max-w-none">{checklist.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {displayChecklists.map((checklist) => (
              <TabsContent key={checklist.id} value={checklist.id} className="mt-4">
                {isEditing && selectedChecklistId === checklist.id ? (
                  <div className="min-w-0 space-y-4 pb-24 md:pb-0">
                      <ChecklistLocalControls
                        onSave={handleSaveEdit}
                        onPreview={() => setIsPreviewOpen(true)}
                        onCancel={handleCancelEdit}
                        onExpandAll={expandAllRooms}
                        onCollapseAll={collapseAllRooms}
                        isSaving={updateChecklist.isPending}
                        isTranslating={isTranslating}
                        allExpanded={collapsedRooms.size === 0}
                        editingChecklistName={editingName.trim() || checklist.name}
                        hasUnsavedChanges={hasUnsavedChanges()}
                        savedHint={savedDistanceHint}
                        showTasksToolbar={checklistBuilderTab === CHECKLIST_BUILDER_TAB.rooms}
                        rooms={rooms}
                        activeRoomIndex={activeRoomTab}
                        onRoomSelect={scrollToRoom}
                        onAddRoom={addRoom}
                      />
                      <Tabs value={checklistBuilderTab} onValueChange={setChecklistBuilderTab} className="w-full">
                        <div className="overflow-x-auto -mx-1 px-1">
                          <TabsList className="w-max min-w-full sm:w-full justify-start rounded-lg bg-muted/80 p-1 gap-1 h-auto flex-nowrap">
                            <TabsTrigger value={CHECKLIST_BUILDER_TAB.basics} className="shrink-0 rounded-full px-3 py-1.5 text-xs sm:text-sm">
                              Basics
                            </TabsTrigger>
                            {editingType === "cleaning" && (
                              <TabsTrigger value={CHECKLIST_BUILDER_TAB.arrival} className="shrink-0 rounded-full px-3 py-1.5 text-xs sm:text-sm">
                                Arrival
                              </TabsTrigger>
                            )}
                            <TabsTrigger value={CHECKLIST_BUILDER_TAB.proof} className="shrink-0 rounded-full px-3 py-1.5 text-xs sm:text-sm">
                              Unit Check
                            </TabsTrigger>
                            <TabsTrigger value={CHECKLIST_BUILDER_TAB.rooms} className="shrink-0 rounded-full px-3 py-1.5 text-xs sm:text-sm">
                              Tasks
                            </TabsTrigger>
                            <TabsTrigger value={CHECKLIST_BUILDER_TAB.guest} className="shrink-0 rounded-full px-3 py-1.5 text-xs sm:text-sm">
                              Guest Amenities
                            </TabsTrigger>
                            {rooms.length > 0 && (
                              <TabsTrigger value={CHECKLIST_BUILDER_TAB.reference} className="shrink-0 rounded-full px-3 py-1.5 text-xs sm:text-sm">
                                Photos
                              </TabsTrigger>
                            )}
                          </TabsList>
                        </div>
                      <TabsContent value={CHECKLIST_BUILDER_TAB.basics} forceMount className="mt-3 data-[state=inactive]:hidden">
                    <Card>
                      <CardHeader>
                        <CardTitle>Edit Checklist</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Checklist Name</Label>
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              placeholder="Standard Turnover"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Type</Label>
                            <Select value={editingType} onValueChange={(v) => setEditingType(v as ChecklistType)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(Object.keys(CHECKLIST_TYPE_LABELS) as ChecklistType[]).map((t) => (
                                  <SelectItem key={t} value={t}>
                                    {CHECKLIST_TYPE_LABELS[t]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label>Details (optional)</Label>
                            <Input
                              value={editingDescription}
                              onChange={(e) => setEditingDescription(e.target.value)}
                              placeholder="Brief description..."
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                      </TabsContent>

                      <TabsContent value={CHECKLIST_BUILDER_TAB.arrival} forceMount className="mt-2 data-[state=inactive]:hidden">
                    {editingType === "cleaning" && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Cleaner arrival — entry type &amp; backup key</CardTitle>
                          <p className="text-xs text-muted-foreground mt-1">
                            Cleaners see this as the first steps after they start the visit timer. Match the options to how your property is accessed.
                          </p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label>Default entry type</Label>
                            <Select
                              value={editingAccess.default_entry_type ?? "lockbox"}
                              onValueChange={(v) =>
                                setEditingAccess((a) => ({ ...a, default_entry_type: v as ChecklistEntryType }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="lockbox">Lockbox</SelectItem>
                                <SelectItem value="smart_lock">Smart lock</SelectItem>
                                <SelectItem value="key_handoff">Key handoff</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="space-y-0.5 pr-2">
                              <Label className="text-sm">Cleaner uses their own key</Label>
                              <p className="text-xs text-muted-foreground">
                                When enabled, cleaners must confirm they have their own key before continuing the visit.
                              </p>
                            </div>
                            <Switch
                              checked={!!editingAccess.cleaner_has_own_key}
                              onCheckedChange={(c) => setEditingAccess((a) => ({ ...a, cleaner_has_own_key: c }))}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <Label className="text-sm">Backup key on site</Label>
                            <Switch
                              checked={!!editingAccess.backup_key_required}
                              onCheckedChange={(c) => setEditingAccess((a) => ({ ...a, backup_key_required: c }))}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <Label className="text-sm">Require photo proof of backup key</Label>
                            <Switch
                              checked={!!editingAccess.backup_key_proof_required}
                              onCheckedChange={(c) => setEditingAccess((a) => ({ ...a, backup_key_proof_required: c }))}
                            />
                          </div>
                          <div className="space-y-3 rounded-lg border border-border/80 p-3 bg-muted/20">
                            <div>
                              <Label className="text-sm">Reference photos — where keys are</Label>
                              <p className="text-xs text-muted-foreground mt-1">
                                Cleaners see these on step 1 of the visit (after Start task) so they can match the spot and upload their own photo.
                              </p>
                            </div>
                            <input
                              ref={keyLocationRefInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              disabled={uploadingKeyLocationRefs}
                              onChange={handleKeyLocationRefFiles}
                            />
                            <div className="flex flex-wrap gap-2 items-center">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={uploadingKeyLocationRefs}
                                onClick={() => keyLocationRefInputRef.current?.click()}
                              >
                                {uploadingKeyLocationRefs ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Uploading…
                                  </>
                                ) : (
                                  <>
                                    <ImagePlus className="w-4 h-4 mr-2" />
                                    Add reference photo
                                  </>
                                )}
                              </Button>
                            </div>
                            {(editingAccess.key_location_reference_urls ?? []).length > 0 && (
                              <>
                              <div className="flex flex-wrap gap-2">
                                {(editingAccess.key_location_reference_urls ?? []).map((url) => (
                                  <div
                                    key={url}
                                    className="relative w-20 h-20 rounded-md border bg-background overflow-hidden shrink-0 group"
                                  >
                                    <img src={url} alt="" className="w-full h-full object-cover" />
                                    <button
                                      type="button"
                                      className="absolute top-1 right-1 rounded-full bg-background/90 p-1 shadow border opacity-90 hover:opacity-100"
                                      aria-label="Remove reference photo"
                                      onClick={() => void handleRemoveKeyLocationRef(url)}
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <p className="text-[11px] text-muted-foreground">
                                Save the checklist so these appear on the cleaner&apos;s arrival step.
                              </p>
                              </>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Notes for cleaners (optional)</Label>
                            <Textarea
                              value={editingAccess.notes ?? ""}
                              onChange={(e) => setEditingAccess((a) => ({ ...a, notes: e.target.value || null }))}
                              rows={2}
                              placeholder="e.g. spare in lockbox B12"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    )}
                      </TabsContent>

                      <TabsContent value={CHECKLIST_BUILDER_TAB.guest} forceMount className="mt-2 data-[state=inactive]:hidden">
                      <div className="space-y-4">
                        <Card
                          id={`checklist-edit-guest-amenities-${checklist.id}`}
                          className="mb-0 scroll-mt-32 md:scroll-mt-40"
                        >
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Guest Amenities</CardTitle>
                            <p className="text-sm font-normal text-muted-foreground leading-relaxed">
                              Tell your cleaner how many items to leave out and where they should go.
                            </p>
                            <p className="text-sm font-normal text-muted-foreground leading-relaxed">
                              <span className="font-medium text-foreground">Pro tip:</span> Add reference photos so
                              cleaners can visually see how to leave items out.
                            </p>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <GuestAmenityStagingEditor
                              propertyId={propertyId}
                              checklistId={checklist.id}
                              lines={editingGuestAmenityStaging}
                              onLinesChange={setEditingGuestAmenityStaging}
                            />
                          </CardContent>
                        </Card>
                      </div>
                      </TabsContent>

                      <TabsContent value={CHECKLIST_BUILDER_TAB.proof} forceMount className="mt-2 data-[state=inactive]:hidden">
                      {editingType === "cleaning" && (
                        <AiGenerateChecklistBanner
                          photoRoomCount={listingPhotoRoomCount}
                          onGenerate={() => setAiGenerateOpen(true)}
                          className="mb-3"
                        />
                      )}
                      <div
                        id={`checklist-edit-proof-standards-${checklist.id}`}
                        className="scroll-mt-32 md:scroll-mt-40"
                      >
                        <StandardsAndProofSection
                          propertyId={propertyId}
                          checklistId={checklist.id}
                          roomNames={rooms.map((r) => r.name)}
                          sectionTitle="Photo list"
                          sectionDescription="Shots cleaners must take before and after every clean. Add titles for this property — they photograph each one."
                          listEmptyHint="No photo shots yet. Add what you need proof of (kitchen counters, bath, closets…)."
                        />
                      </div>
                      </TabsContent>

                      <TabsContent value={CHECKLIST_BUILDER_TAB.rooms} forceMount className="mt-2 data-[state=inactive]:hidden">
                    {editingType === "cleaning" && (
                      <AiGenerateChecklistBanner
                        photoRoomCount={listingPhotoRoomCount}
                        onGenerate={() => setAiGenerateOpen(true)}
                        className="mb-3"
                      />
                    )}
                    <p className="text-xs text-muted-foreground">
                      Each task lives under a room. Manage property rooms on the{" "}
                      <button
                        type="button"
                        className="underline hover:no-underline"
                        onClick={() => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set("tab", "rooms"); return n; })}
                      >
                        Rooms
                      </button>{" "}
                      tab if needed.
                    </p>

                    {/* Rooms Section */}
                    <div id={`checklist-edit-rooms-tasks-${checklist.id}`} className="space-y-3 scroll-mt-32 md:scroll-mt-40">
                      {rooms.length === 0 ? (
                        <Card className="border-dashed bg-muted/30">
                          <CardContent className="flex flex-col items-center justify-center py-12 px-6 text-center">
                            <p className="text-sm font-medium text-muted-foreground mb-1">No rooms yet</p>
                            <p className="text-xs text-muted-foreground mb-4 max-w-sm">
                              Add a room to organize tasks (e.g. Kitchen, Bathroom). You can add tasks to each room next.
                            </p>
                            <Button onClick={addRoom} className="gap-2">
                              <Plus className="h-4 w-4" />
                              Add your first room
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                      rooms.map((room, roomIndex) => (
                          <Card 
                            key={roomIndex} 
                            id={`room-${roomIndex}`}
                            ref={(el) => { roomRefs.current[roomIndex] = el; }}
                            className={`scroll-mt-32 md:scroll-mt-40 transition-all ${
                              newlyAddedRoomIndex === roomIndex ? 'animate-room-highlight' : ''
                            }`}
                          >
                            <Collapsible 
                              open={!collapsedRooms.has(roomIndex)} 
                              onOpenChange={() => toggleRoomCollapsed(roomIndex)}
                            >
                              <div className="flex items-center gap-2 p-3">
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
                                    {collapsedRooms.has(roomIndex) ? (
                                      <ChevronDown className="w-4 h-4" />
                                    ) : (
                                      <ChevronUp className="w-4 h-4" />
                                    )}
                                  </Button>
                                </CollapsibleTrigger>
                                <div className="flex-1 font-medium text-sm">
                                  {room.name.trim() || `Room ${roomIndex + 1}`}
                                </div>
                                <Badge variant="secondary" className="shrink-0 text-xs">
                                  {(room.tasks || []).length} task{(room.tasks || []).length !== 1 ? 's' : ''}
                                </Badge>
                                <RoomManagementMenu
                                  roomIndex={roomIndex}
                                  totalRooms={rooms.length}
                                  onMoveUp={() => moveRoomUp(roomIndex)}
                                  onMoveDown={() => moveRoomDown(roomIndex)}
                                  onRename={() => setRenameRoomIndex(roomIndex)}
                                  onDelete={() => setDeleteRoomIndex(roomIndex)}
                                  onDuplicate={() => duplicateRoom(roomIndex)}
                                />
                              </div>
                              <CollapsibleContent>
                                <div className="space-y-3 px-3 pb-3 pl-7">
                              {(room.tasks || []).map((task, taskIndex) => {
                                const taskObj = normalizeTask(task);
                                const taskKey = getTaskPhotoKey(room.name, taskIndex);
                                const taskPhotosForTask = getTaskPhotos(room.name, taskObj.text);
                                const isUploading = uploadingTask === taskKey;

                                return (
                                  <div 
                                    key={taskIndex} 
                                    className="space-y-3 p-3 rounded-lg border bg-muted/30 transition-all duration-200"
                                  >
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      {/* Reorder buttons - vertical stack on mobile for 44px touch targets */}
                                      <div className="flex flex-col sm:flex-row gap-0.5 shrink-0">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 sm:h-7 sm:w-7 min-w-[32px] sm:min-w-0 touch-manipulation"
                                          onClick={() => moveTaskUp(roomIndex, taskIndex)}
                                          disabled={taskIndex === 0}
                                          title="Move up"
                                        >
                                          <ChevronUp className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 sm:h-7 sm:w-7 min-w-[32px] sm:min-w-0 touch-manipulation"
                                          onClick={() => moveTaskDown(roomIndex, taskIndex)}
                                          disabled={taskIndex === (room.tasks || []).length - 1}
                                          title="Move down"
                                        >
                                          <ChevronDown className="w-4 h-4" />
                                        </Button>
                                      </div>
                                      
                                      <span className="text-sm font-medium text-muted-foreground min-w-[24px]">
                                        {taskIndex + 1}.
                                      </span>
                                      <Input
                                        value={taskObj.text}
                                        onChange={(e) => updateTaskText(roomIndex, taskIndex, e.target.value)}
                                        placeholder="Task description"
                                        className="flex-1"
                                      />
                                      {/* Task Action Buttons - only show if task has text and there are other rooms */}
                                      {taskObj.text.trim() && rooms.filter(r => r.name.trim() && r.name !== room.name).length > 0 && (
                                        <>
                                          <Button 
                                            variant="ghost" 
                                            size="icon"
                                            className="h-10 w-10 min-w-[40px] shrink-0 hidden sm:flex"
                                            title="Apply to multiple rooms"
                                            onClick={() => setApplyToRoomsData({ task: taskObj, roomName: room.name })}
                                          >
                                            <LayoutGrid className="w-4 h-4" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="icon"
                                            className="h-10 w-10 min-w-[40px] shrink-0 hidden sm:flex"
                                            title="Move to another room"
                                            onClick={() => setMoveTaskData({ task: taskObj, roomName: room.name })}
                                          >
                                            <MoveRight className="w-4 h-4" />
                                          </Button>
                                        </>
                                      )}
                                      
                                      {/* More actions dropdown */}
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-10 w-10 min-w-[40px] shrink-0"
                                          >
                                            <MoreVertical className="w-4 h-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                          <DropdownMenuItem 
                                            onClick={() => moveTaskToTop(roomIndex, taskIndex)}
                                            disabled={taskIndex === 0}
                                          >
                                            <ChevronsUp className="w-4 h-4 mr-2" />
                                            Move to top
                                          </DropdownMenuItem>
                                          <DropdownMenuItem 
                                            onClick={() => moveTaskToBottom(roomIndex, taskIndex)}
                                            disabled={taskIndex === (room.tasks || []).length - 1}
                                          >
                                            <ChevronsDown className="w-4 h-4 mr-2" />
                                            Move to bottom
                                          </DropdownMenuItem>
                                          
                                          {taskObj.text.trim() && rooms.filter(r => r.name.trim() && r.name !== room.name).length > 0 && (
                                            <>
                                              <DropdownMenuSeparator />
                                              <DropdownMenuItem onClick={() => setApplyToRoomsData({ task: taskObj, roomName: room.name })}>
                                                <LayoutGrid className="w-4 h-4 mr-2" />
                                                Apply to rooms
                                              </DropdownMenuItem>
                                              <DropdownMenuItem onClick={() => setMoveTaskData({ task: taskObj, roomName: room.name })}>
                                                <MoveRight className="w-4 h-4 mr-2" />
                                                Move to room
                                              </DropdownMenuItem>
                                            </>
                                          )}
                                          
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem 
                                            onClick={() => removeTask(roomIndex, taskIndex)}
                                            className="text-destructive focus:text-destructive"
                                          >
                                            <X className="w-4 h-4 mr-2" />
                                            Delete task
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                    
                                    {/* Task settings (by checklist type; no per-task type) */}
                                    <div className="space-y-3 pt-2 border-t">
                                      {/* Cleaning category (only for cleaning checklists) */}
                                      {editingType === "cleaning" && (
                                        <div className="space-y-2">
                                          <p className="text-xs font-medium text-muted-foreground">Cleaning category</p>
                                          <Select
                                            value={((taskObj as TaskObject).subcategory as CleaningCategory) || "auto"}
                                            onValueChange={(value) => updateTaskCleaningCategory(roomIndex, taskIndex, value as CleaningCategory)}
                                          >
                                            <SelectTrigger className="w-full sm:w-[200px]">
                                              <SelectValue placeholder="Auto" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {(Object.keys(CLEANING_CATEGORY_LABELS) as CleaningCategory[]).map((cat) => (
                                                <SelectItem key={cat} value={cat}>
                                                  {CLEANING_CATEGORY_LABELS[cat]}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {(((taskObj as TaskObject).subcategory as string) || "auto") === "auto" && (
                                            <p className="text-xs text-muted-foreground">Auto uses task text to suggest a category.</p>
                                          )}
                                          {((taskObj as TaskObject).subcategory as string) === "guest_amenities" && (
                                            <div className="space-y-1.5 rounded-md border border-border/80 bg-muted/30 p-3">
                                              <Label htmlFor={`guest-qty-${taskKey}`} className="text-xs font-medium">
                                                How many to leave out each turnover
                                              </Label>
                                              <Input
                                                id={`guest-qty-${taskKey}`}
                                                type="number"
                                                inputMode="numeric"
                                                min={1}
                                                max={999}
                                                placeholder="e.g. 2"
                                                className="h-9 w-full max-w-[120px] sm:w-[120px]"
                                                value={
                                                  (taskObj as TaskObject).guest_restock_quantity != null
                                                    ? String((taskObj as TaskObject).guest_restock_quantity)
                                                    : ""
                                                }
                                                onChange={(e) =>
                                                  updateTaskGuestRestockQuantity(roomIndex, taskIndex, e.target.value)
                                                }
                                              />
                                              <p className="text-[11px] text-muted-foreground leading-snug">
                                                Cleaners see this on their checklist before the guest-amenities inventory step.
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Cleaning Task Settings */}
                                      {editingType === 'cleaning' && (
                                        <div className="space-y-2">
                                          <p className="text-xs font-medium text-muted-foreground">Cleaning Photo Settings</p>
                                          
                                          {/* Reference Photo Toggle */}
                                          <div className="grid grid-cols-[1fr_auto] items-center gap-2 py-1">
                                            <label htmlFor={`ref-${taskKey}`} className="flex items-start gap-2 text-sm cursor-pointer">
                                              <ImageIcon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                              <span className="leading-tight">Reference Photo</span>
                                            </label>
                                            <Switch
                                              id={`ref-${taskKey}`}
                                              checked={taskObj.require_reference_photo}
                                              onCheckedChange={(checked) => 
                                                updateTaskPhotoRequirement(roomIndex, taskIndex, 'require_reference_photo', checked)
                                              }
                                            />
                                          </div>
                                          
                                          {/* Reference Photo Gallery - Only show when toggle is ON */}
                                          {taskObj.require_reference_photo && (
                                            <TaskReferencePhotoGallery
                                              photos={taskPhotosForTask}
                                              roomName={room.name}
                                              taskText={taskObj.text}
                                              isUploading={isUploading}
                                              onUpload={(files) => handleTaskPhotoUpload(room.name, taskObj.text, taskKey, files)}
                                              onDelete={handleDeleteTaskPhoto}
                                            />
                                          )}
                                          
                                          {/* Before Photo Toggle */}
                                          <div className="grid grid-cols-[1fr_auto] items-center gap-2 py-1">
                                            <label htmlFor={`before-${taskKey}`} className="flex items-start gap-2 text-sm cursor-pointer">
                                              <ImagePlus className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                              <span className="leading-tight">Cleaner BEFORE Photo</span>
                                            </label>
                                            <Switch
                                              id={`before-${taskKey}`}
                                              checked={taskObj.require_before_photo}
                                              onCheckedChange={(checked) => 
                                                updateTaskPhotoRequirement(roomIndex, taskIndex, 'require_before_photo', checked)
                                              }
                                            />
                                          </div>
                                          
                                          {/* After Photo Toggle */}
                                          <div className="grid grid-cols-[1fr_auto] items-center gap-2 py-1">
                                            <label htmlFor={`after-${taskKey}`} className="flex items-start gap-2 text-sm cursor-pointer">
                                              <ImagePlus className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                              <span className="leading-tight">Cleaner AFTER Photo</span>
                                            </label>
                                            <Switch
                                              id={`after-${taskKey}`}
                                              checked={taskObj.require_after_photo}
                                              onCheckedChange={(checked) => 
                                                updateTaskPhotoRequirement(roomIndex, taskIndex, 'require_after_photo', checked)
                                              }
                                            />
                                          </div>
                                        </div>
                                      )}

                                      {/* Maintenance Task Settings */}
                                      {editingType === 'maintenance' && (
                                        <div className="space-y-3">
                                          <p className="text-xs font-medium text-muted-foreground">Maintenance Settings</p>
                                          
                                          {/* Question Field */}
                                          <div className="space-y-1.5">
                                            <label htmlFor={`question-${taskKey}`} className="flex items-start gap-2 text-sm">
                                              <HelpCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                              <span className="leading-tight">Yes/No Question for Worker</span>
                                            </label>
                                            <Input
                                              id={`question-${taskKey}`}
                                              placeholder="Is the thermostat functioning properly?"
                                              value={getMaintenanceSettings(taskObj).question || ''}
                                              onChange={(e) => 
                                                updateMaintenanceSetting(roomIndex, taskIndex, 'question', e.target.value)
                                              }
                                            />
                                          </div>
                                          
                                          {/* Require Photos Toggle */}
                                          <div className="grid grid-cols-[1fr_auto] items-center gap-2 py-1">
                                            <label htmlFor={`maint-photos-${taskKey}`} className="flex items-start gap-2 text-sm cursor-pointer">
                                              <Camera className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                              <span className="leading-tight">Require photos</span>
                                            </label>
                                            <Switch
                                              id={`maint-photos-${taskKey}`}
                                              checked={getMaintenanceSettings(taskObj).require_photos}
                                              onCheckedChange={(checked) => 
                                                updateMaintenanceSetting(roomIndex, taskIndex, 'require_photos', checked)
                                              }
                                            />
                                          </div>
                                          
                                          {/* Allow Notes Toggle */}
                                          <div className="grid grid-cols-[1fr_auto] items-center gap-2 py-1">
                                            <label htmlFor={`notes-${taskKey}`} className="flex items-start gap-2 text-sm cursor-pointer">
                                              <Edit2 className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                              <span className="leading-tight">Allow notes</span>
                                            </label>
                                            <Switch
                                              id={`notes-${taskKey}`}
                                              checked={getMaintenanceSettings(taskObj).allow_notes}
                                              onCheckedChange={(checked) => 
                                                updateMaintenanceSetting(roomIndex, taskIndex, 'allow_notes', checked)
                                              }
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              <Button variant="ghost" size="sm" onClick={() => addTask(roomIndex)}>
                                <Plus className="w-4 h-4 mr-1" /> Add Task
                              </Button>
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          </Card>
                      ))
                      )}
                    </div>
                      </TabsContent>

                      <TabsContent value={CHECKLIST_BUILDER_TAB.reference} forceMount className="mt-2 data-[state=inactive]:hidden">
                        {rooms.length > 0 && (
                          <div id="checklist-edit-reference-photos" className="scroll-mt-32 md:scroll-mt-40">
                            <ReferencePhotosSection
                              propertyId={propertyId}
                              rooms={rooms}
                              checklistId={checklist.id}
                            />
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </div>
                ) : (
                  // View Mode
                  <div className="min-w-0">
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="text-lg">{checklist.name}</CardTitle>
                            {checklist.is_default && (
                              <Badge variant="secondary" className="gap-1">
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                Default
                              </Badge>
                            )}
                          </div>
                          {checklist.description && (
                            <p className="text-sm text-muted-foreground">{checklist.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setIsPreviewOpen(true)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Preview
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => startEditing(checklist)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setDeleteChecklistId(checklist.id)}
                            disabled={displayChecklists.length === 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Set as Default Toggle */}
                      {!checklist.is_default && (
                        <div className="flex items-center justify-between py-3 mb-4 border-b">
                          <div>
                            <p className="text-sm font-medium">Set as Default</p>
                            <p className="text-xs text-muted-foreground">Used for new cleaning tasks</p>
                          </div>
                          <Switch
                            checked={false}
                            onCheckedChange={() => handleSetDefault(checklist.id)}
                            disabled={setDefaultChecklist.isPending}
                          />
                        </div>
                      )}

                      {checklist.type !== "maintenance" &&
                        (() => {
                          const viewAccess = parseAccessVerificationSettings(
                            checklist.access_verification_settings
                          );
                          const keyRefs = viewAccess.key_location_reference_urls ?? [];
                          if (keyRefs.length === 0) return null;
                          return (
                            <div className="mb-4 rounded-lg border border-border/80 bg-muted/20 p-3">
                              <p className="text-sm font-medium">Key location reference photos</p>
                              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                                Cleaners see these on arrival before they upload their own proof.
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {keyRefs.map((url) => (
                                  <a
                                    key={url}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="relative block h-20 w-20 shrink-0 overflow-hidden rounded-md border bg-background"
                                  >
                                    <img src={url} alt="" className="h-full w-full object-cover" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                      {/* Rooms & Tasks Display */}
                      {selectedRooms.length === 0 ? (
                        <p className="text-muted-foreground text-sm">No rooms added yet. Click edit to add rooms and tasks.</p>
                      ) : (
                        selectedRooms.map((room, i) => (
                          <div key={i} className="mb-3">
                            <p className="font-medium">{room.name}</p>
                            <ul className="text-sm text-muted-foreground space-y-1">
                              {(room.tasks || []).map((task, j) => {
                                const taskTextValue = getTaskText(task);
                                const taskPhotosForTask = getTaskPhotos(room.name, taskTextValue);
                                return (
                                  <li key={j} className="flex items-center gap-2 pl-1 sm:pl-0">
                                    <span className="text-foreground font-medium min-w-[24px]">{j + 1}.</span>
                                    <span>{taskTextValue}</span>
                                    {taskPhotosForTask.length > 0 && (
                                      <button
                                        onClick={() => setViewingPhoto(taskPhotosForTask[0].photo_url)}
                                        className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded hover:bg-primary/20 transition-colors"
                                      >
                                        <CheckCircle2 className="w-3 h-3" />
                                        <span>{taskPhotosForTask.length} Photo{taskPhotosForTask.length > 1 ? 's' : ''}</span>
                                      </button>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                  </div>
                )}

              </TabsContent>
            ))}
          </Tabs>

          {/* Reference Photos Section (scoped to selected checklist; hidden while editing — builder Photos tab) */}
          {selectedChecklistId && !isEditing && selectedRooms.length > 0 && (
            <div id="checklist-edit-reference-photos" className="mt-10 scroll-mt-32 md:scroll-mt-40">
              <ReferencePhotosSection propertyId={propertyId} rooms={selectedRooms} checklistId={selectedChecklistId} />
            </div>
          )}
        </>
      )}

      {/* Create Checklist Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Checklist</DialogTitle>
            <DialogDescription className="sr-only">
              Choose a name, type, optional details, and which property rooms to include.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newChecklistName}
                onChange={(e) => setNewChecklistName(e.target.value)}
                placeholder="e.g., Deep Clean, Owner Arrival"
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={newChecklistType} onValueChange={(v) => setNewChecklistType(v as ChecklistType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CHECKLIST_TYPE_LABELS) as ChecklistType[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {CHECKLIST_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Details (optional)</Label>
              <Textarea
                value={newChecklistDescription}
                onChange={(e) => setNewChecklistDescription(e.target.value)}
                placeholder="Brief description of this checklist..."
                rows={2}
              />
            </div>
            {checklists && checklists.length > 0 && (
              <div className="space-y-2">
                <Label>Start from existing checklist?</Label>
                <Select value={cloneFromId} onValueChange={setCloneFromId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Start fresh (empty)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Start fresh (empty)</SelectItem>
                    {displayChecklists.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <Copy className="w-3 h-3" />
                          Clone from "{c.name}"
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(cloneFromId === "none" || !cloneFromId) && (
              <div className="space-y-2">
                <Label>Rooms (from this property)</Label>
                <p className="text-xs text-muted-foreground">Pick existing rooms or add a new one. They’ll appear on the checklist.</p>
                <div className="flex flex-wrap gap-2 border rounded-md p-2 min-h-[44px]">
                  {propertyRoomsList.map((pr) => {
                    const selected = createModalSelectedRoomIds.includes(pr.id);
                    return (
                      <button
                        key={pr.id}
                        type="button"
                        onClick={() =>
                          setCreateModalSelectedRoomIds((prev) =>
                            selected ? prev.filter((id) => id !== pr.id) : [...prev, pr.id]
                          )
                        }
                        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm border transition-colors ${
                          selected ? "bg-primary/15 border-primary text-primary" : "bg-muted/50 border-transparent hover:bg-muted"
                        }`}
                      >
                        {pr.name}
                        {selected && <CheckCircle2 className="w-3.5 h-3.5" />}
                      </button>
                    );
                  })}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCreateModalRoomPickerOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add room
                </Button>
                <RoomPickerModal
                  open={createModalRoomPickerOpen}
                  onOpenChange={setCreateModalRoomPickerOpen}
                  propertyId={propertyId!}
                  mode="multi"
                  selectedIds={createModalSelectedRoomIds}
                  title="Add room"
                  prompt="Select rooms to include in this checklist, or create a new room."
                  onSelect={(selectedRooms) => {
                    setCreateModalSelectedRoomIds(selectedRooms.map((r) => r.id));
                  }}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCreateModal}>
              Cancel
            </Button>
            <Button onClick={handleCreateChecklist} disabled={createChecklist.isPending}>
              {createChecklist.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Create Checklist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full image viewer dialog */}
      <Dialog open={!!viewingPhoto} onOpenChange={() => setViewingPhoto(null)}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="sr-only">Reference Photo</DialogTitle>
          {viewingPhoto && (
            <div className="flex items-center justify-center min-h-[200px]">
              <img
                src={viewingPhoto}
                alt="Reference photo"
                className="w-full h-auto max-h-[70vh] object-contain rounded"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = "none";
                  target.parentElement!.innerHTML = `
                    <div class="text-center p-4">
                      <p class="text-destructive mb-2">Failed to load image</p>
                      <p class="text-xs text-muted-foreground break-all">${viewingPhoto}</p>
                    </div>
                  `;
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checklist Preview Modal - uses current editing state if editing, otherwise saved state */}
      <ChecklistPreviewModal
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        checklistName={isEditing ? editingName : (selectedChecklist?.name || "")}
        rooms={isEditing ? rooms : ((selectedChecklist?.rooms as Room[]) || [])}
        propertyId={propertyId}
        checklistId={selectedChecklistId}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteChecklistId} onOpenChange={(open) => !open && setDeleteChecklistId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Checklist</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{displayChecklists.find(c => c.id === deleteChecklistId)?.name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Task Modal */}
      {moveTaskData && (
        <MoveTaskModal
          isOpen={!!moveTaskData}
          onClose={() => setMoveTaskData(null)}
          task={moveTaskData.task}
          currentRoomName={moveTaskData.roomName}
          rooms={rooms}
          onMoveTask={handleMoveTask}
        />
      )}

      {/* Apply to Multiple Rooms Modal */}
      <ApplyToRoomsModal
        open={!!applyToRoomsData}
        onOpenChange={(open) => !open && setApplyToRoomsData(null)}
        taskText={applyToRoomsData?.task.text || ""}
        currentRoomName={applyToRoomsData?.roomName || ""}
        rooms={rooms}
        onApply={(selectedRoomNames, skippedRoomNames) => {
          if (!applyToRoomsData) return;
          
          // Only apply to rooms that weren't skipped
          if (selectedRoomNames.length === 0) {
            setApplyToRoomsData(null);
            if (skippedRoomNames.length > 0) {
              toast({
                title: "No tasks added",
                description: `Skipped ${skippedRoomNames.length} room${skippedRoomNames.length !== 1 ? "s" : ""} with duplicate tasks`,
              });
            }
            return;
          }
          
          const taskToDuplicate = applyToRoomsData.task;
          const updatedRooms = [...rooms];
          const taskTemplate = normalizeTask(taskToDuplicate as Task);
          
          // Duplicate task to each selected room (excluding skipped)
          selectedRoomNames.forEach((roomName) => {
            const roomIndex = updatedRooms.findIndex((r) => r.name === roomName);
            if (roomIndex !== -1) {
              // Create a deep copy of the task to ensure independence
              const duplicatedTask: TaskObject = { ...taskTemplate };
              
              // Add to end of room's tasks array (never overwrites)
              updatedRooms[roomIndex].tasks = [
                ...updatedRooms[roomIndex].tasks,
                duplicatedTask,
              ];
            }
          });
          
          setRooms(updatedRooms);
          setApplyToRoomsData(null);
          
          // Build toast message
          let description = `Task added to ${selectedRoomNames.length} room${selectedRoomNames.length !== 1 ? "s" : ""}`;
          if (skippedRoomNames.length > 0) {
            description += `. Skipped ${skippedRoomNames.length} duplicate${skippedRoomNames.length !== 1 ? "s" : ""}.`;
          }
          
          toast({
            title: "Task applied",
            description,
          });
        }}
      />

      {/* Add Room: pick from canonical list (Rooms tab) or create new */}
      <RoomPickerModal
        open={isAddRoomModalOpen}
        onOpenChange={setIsAddRoomModalOpen}
        propertyId={propertyId!}
        mode="single"
        title="Add room"
        prompt="Which room do you want to add?"
        onSelect={(selectedRooms) => {
          if (selectedRooms.length > 0) addRoomFromPicker(selectedRooms[0]);
        }}
      />

      {/* Delete Room Confirmation Dialog */}
      {deleteRoomIndex !== null && (
        <DeleteRoomDialog
          open={true}
          onOpenChange={(open) => !open && setDeleteRoomIndex(null)}
          roomName={rooms[deleteRoomIndex]?.name || ""}
          taskCount={rooms[deleteRoomIndex]?.tasks.length || 0}
          onConfirm={() => removeRoom(deleteRoomIndex)}
        />
      )}

      {/* Rename / Edit Room Dialog */}
      {renameRoomIndex !== null && (
        <RenameRoomDialog
          open={true}
          onOpenChange={(open) => !open && setRenameRoomIndex(null)}
          currentName={rooms[renameRoomIndex]?.name || ""}
          currentEntryCode={rooms[renameRoomIndex]?.entry_code}
          currentEntryNotes={rooms[renameRoomIndex]?.entry_notes}
          onRename={(newName, entryCode, entryNotes) => {
            updateRoomDetails(renameRoomIndex, {
              name: newName,
              entry_code: entryCode,
              entry_notes: entryNotes,
            });
            setRenameRoomIndex(null);
          }}
        />
      )}

      {isEditing && selectedChecklistId && (
        <GenerateChecklistFromPhotosModal
          open={aiGenerateOpen}
          onOpenChange={setAiGenerateOpen}
          propertyId={propertyId}
          photos={listingScanPhotos}
          onApply={handleAiGenerateApply}
          isApplying={aiGenerateApplying}
        />
      )}

      {/* Editing Guard Dialog - unsaved progress when switching or canceling */}
      <EditingGuardDialog
        open={pendingChecklistSwitch !== null || pendingCreateChecklist || pendingCancelExit}
        onOpenChange={(open) => {
          if (!open) {
            setPendingChecklistSwitch(null);
            setPendingCreateChecklist(false);
            setPendingCancelExit(false);
          }
        }}
        onStay={handleStayEditing}
        onDiscard={() => {
          if (pendingCancelExit) {
            setPendingCancelExit(false);
            resetEditForm();
          } else {
            handleDiscardAndSwitch();
          }
        }}
      />

      {isEditing && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur-sm pt-2 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_24px_rgba(0,0,0,0.06)] md:hidden"
          role="region"
          aria-label="Checklist save actions"
        >
          <div className="max-w-6xl mx-auto flex items-center gap-2">
            <Button className="flex-1" size="sm" onClick={handleSaveEdit} disabled={updateChecklist.isPending || isTranslating}>
              <Save className="w-4 h-4 mr-2" />
              {isTranslating ? "Translating..." : updateChecklist.isPending ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsPreviewOpen(true)} aria-label="Preview checklist">
              <Eye className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistTab;
